import React from 'react';
import { API_BASE } from '../api/api';
import './ContentRenderer.css';

const ContentRenderer = ({ content }) => {
  if (!content) return null;

  let blocks;
  try {
    blocks = typeof content === 'string' ? JSON.parse(content) : content;
  } catch {
    // Fallback: render as plain text paragraphs
    return (
      <div className="content-renderer">
        {content.split('\n\n').map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    );
  }

  if (!Array.isArray(blocks)) return null;

  // Track background image for next block
  let nextBackgroundImage = null;

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  const renderBlock = (block, index) => {
    // If there is a background image set for this block, wrap it
    const wrapWithBackground = (element) => {
      if (nextBackgroundImage) {
        const bgUrl = resolveUrl(nextBackgroundImage.url);
        const bgMode = nextBackgroundImage.bgMode || 'cover';
        nextBackgroundImage = null; // Reset after use
        return (
          <div
            key={index}
            className="content-bg-block"
            style={{
              position: 'relative',
              zIndex: 1,
              margin: '32px 0',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${bgUrl})`,
                backgroundSize: bgMode,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                opacity: 0.35,
                zIndex: 0,
                borderRadius: 4,
              }}
              aria-hidden="true"
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              {element}
            </div>
          </div>
        );
      }
      return element;
    };

    switch (block.type) {
      case 'list': {
        // block.items: array of strings or objects with 'text'
        // block.ordered: boolean (true for <ol>, false for <ul>)
        const items = (block.items || []).map((item, i) => {
          const text = typeof item === 'string' ? item : item.text || '';
          return <li key={i} dangerouslySetInnerHTML={{ __html: formatInlineText(text) }} />;
        });
        const ListTag = block.ordered ? 'ol' : 'ul';
        return wrapWithBackground(
          <ListTag key={index} className="content-list">
            {items}
          </ListTag>
        );
      }
      case 'paragraph':
        return wrapWithBackground(
          <p key={index} dangerouslySetInnerHTML={{ __html: formatInlineText(block.text || '') }} />
        );

      case 'heading':
        const HeadingTag = `h${block.level || 2}`;
        return wrapWithBackground(
          <HeadingTag key={index}>{block.text}</HeadingTag>
        );

      case 'image':
        if (block.backgroundForNext) {
          nextBackgroundImage = {
            url: block.url,
            bgMode: block.bgMode || 'cover',
          };
          // Do NOT render the image itself if it's used as background
          return null;
        }
        return (
          <figure key={index} className="content-image">
            <img src={resolveUrl(block.url)} alt={block.caption || ''} loading="lazy" />
            {block.caption && <figcaption>{block.caption}</figcaption>}
          </figure>
        );

      case 'audio':
        return (
          <div key={index} className="content-audio">
            <div className="audio-label">{block.title || 'SES KAYDI'}</div>
            <audio controls preload="metadata">
              <source src={resolveUrl(block.url)} />
              Tarayıcınız ses oynatmayı desteklemiyor.
            </audio>
            {block.caption && <p className="audio-caption">{block.caption}</p>}
          </div>
        );

      case 'video':
        return (
          <div key={index} className="content-video">
            <div className="video-label">{block.title || 'VİDEO'}</div>
            <video controls preload="metadata" playsInline>
              <source src={resolveUrl(block.url)} />
              Tarayıcınız video oynatmayı desteklemiyor.
            </video>
          </div>
        );

      case 'table':
        return (
          <div key={index} className="content-table-wrapper">
            <table className="content-table">
              {block.headers && block.headers.length > 0 && (
                <thead>
                  <tr>
                    {block.headers.map((h, i) => <th key={i}>{h}</th>)}
                  </tr>
                </thead>
              )}
              <tbody>
                {(block.rows || []).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'code':
        return (
          <pre key={index} className="content-code">
            <code>{block.text}</code>
          </pre>
        );

      case 'divider':
        return <hr key={index} className="content-divider" />;

      default:
        return null;
    }
  };

  return (
    <div className="content-renderer">
      {blocks.map((block, index) => renderBlock(block, index))}
    </div>
  );
};

/**
 * Format inline text: **bold**, *italic*
 */
function formatInlineText(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

export default ContentRenderer;
